// ADOPS-3075 — A line item's budget and bonus are only delivered to the extent the
// custom flighting profiles allocate them, so anything left over silently never
// flights. The same shortfall is reported in two places — the confirmation shown when
// saving the flighting modal, and a standing warning on the line item form — so the
// wording lives here rather than being written twice.

import currency from 'currency.js';

import { addCommas } from './helpers';

export interface AllocationTotals {
  totalImpressions: number;
  totalBonus: number;
  impressionBudgetCap: number;
  bonusCap: number;
}

const CONSEQUENCE = "Flighting only delivers what these profiles allocate, so the leftover impressions won't be delivered.";

/**
 * One line per under-allocated figure, empty when everything is allocated. A cap of 0
 * means the line item has no budget/bonus to allocate yet, which is not a shortfall.
 */
export const getAllocationShortfalls = ({
  totalImpressions,
  totalBonus,
  impressionBudgetCap,
  bonusCap,
}: AllocationTotals): string[] => {
  const impressionShortfall =
    impressionBudgetCap > 0 ? currency(impressionBudgetCap).subtract(totalImpressions).value : 0;
  const bonusShortfall = bonusCap > 0 ? currency(bonusCap).subtract(totalBonus).value : 0;

  return [
    impressionShortfall > 0
      ? `Impression budget: ${addCommas(totalImpressions)} of ${addCommas(impressionBudgetCap)} allocated — ${addCommas(impressionShortfall)} left over.`
      : null,
    bonusShortfall > 0
      ? `Bonus impressions: ${addCommas(totalBonus)} of ${addCommas(bonusCap)} allocated — ${addCommas(bonusShortfall)} left over.`
      : null,
  ].filter(Boolean) as string[];
};

/** The shortfall lines plus what they mean, for a single-paragraph warning. */
export const getAllocationShortfallMessage = (totals: AllocationTotals): string =>
  `${getAllocationShortfalls(totals).join(' ')} ${CONSEQUENCE}`;
